// F4c-3: product detail view (stitch-react).
//
// Read-only detail body rendered inside `product-detail-route`. Shows:
//   - Header row: back link to /products + Edit / Delete action buttons
//   - Two-column layout on lg+: gallery start, metadata stack end.
//   - Single-column stacked layout on < lg: gallery first, metadata below.
//
// Metadata stack:
//   - Status badge (draft / published) — colors come from DESIGN.md
//     (draft = Sand 200 / Sand 600, published = Za'atar wash / Za'atar deep).
//   - Product name — Amiri semibold, large.
//   - Price — Fraunces semibold, Saffron, `formatPrice` output.
//   - Inventory — contextual color + message.
//   - Divider + description block (preserves newlines via `whitespace-pre-wrap`).
//
// The Delete button does NOT render the dialog itself — that is owned
// by F4c-5 (`delete-product-dialog.tsx`). We expose an `onDeleteClick`
// callback and let the route (`product-detail-route.tsx`) lift that
// state + render the dialog when F4c-5 lands.

import { Link } from 'react-router-dom';
import { ChevronLeft, EyeOff, Globe, Pencil, Trash2 } from 'lucide-react';
import { useTranslation, formatPrice, getSalePricing } from '@dukanti/shared';
import type { ProductWithImages } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ProductImageGallery } from './product-image-gallery';

export interface ProductDetailViewProps {
  readonly product: ProductWithImages;
  /** Called when the user clicks the Delete button. The parent route
   *  owns the `DeleteProductDialog` state (F4c-5). */
  readonly onDeleteClick: () => void;
  /** F-028: opens the publish dialog. */
  readonly onPublishClick: () => void;
  /** F-028: opens the unpublish dialog. */
  readonly onUnpublishClick: () => void;
  /** F-028: parent store's `published` flag. Drives the disabled state
   *  of the Publish button. When false, the publish-store-first helper
   *  wins over the inventory helper. */
  readonly storePublished: boolean;
}

const LOW_STOCK_THRESHOLD = 5;

export function ProductDetailView({
  product,
  onDeleteClick,
  onPublishClick,
  onUnpublishClick,
  storePublished,
}: ProductDetailViewProps) {
  const { t, lang } = useTranslation();

  const salePricing = getSalePricing(product);
  const formattedPrice = formatPrice(salePricing.effectivePriceCents, lang);
  const formattedRegularPrice = formatPrice(salePricing.regularPriceCents, lang);
  const editPath = `/products/${product.id}/edit`;

  // Inventory → localized message + color class.
  let inventoryText: string;
  let inventoryClass: string;
  if (product.inventory === 0) {
    inventoryText = t('admin.products.detail.inventoryOutOfStock');
    inventoryClass = 'text-terracotta';
  } else if (product.inventory <= LOW_STOCK_THRESHOLD) {
    inventoryText = t('admin.products.detail.inventoryLow', { count: product.inventory });
    inventoryClass = 'text-saffron';
  } else {
    inventoryText = t('admin.products.detail.inventoryInStock', { count: product.inventory });
    inventoryClass = 'text-sand-500 dark:text-sand-400';
  }

  // Status badge (F-003 V0 always renders draft; kept generic for F-004).
  const statusKey = product.published
    ? 'admin.products.badge.published'
    : 'admin.products.badge.draft';
  const statusVariant = product.published ? 'success' : 'neutral';
  const availabilityKey =
    product.inventory > 0
      ? 'admin.products.table.availability.available'
      : 'admin.products.table.availability.outOfStock';
  const availabilityVariant = product.inventory > 0 ? 'success' : 'neutral';
  const hasCategory = !!product.category?.id;

  return (
    <div className="flex flex-col gap-8" data-testid="product-detail-view">
      {/* Header: back link + actions */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/products"
          className={cn(
            'text-zaatar hover:text-zaatar-deep inline-flex items-center gap-1.5 text-sm font-medium',
            'focus-visible:ring-zaatar rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          )}
          data-testid="product-detail-back"
        >
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" strokeWidth={2} aria-hidden="true" />
          <span>{t('admin.products.detail.backToList')}</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onDeleteClick}
            className="text-sand-600 hover:text-terracotta dark:text-sand-300"
            data-testid="product-detail-delete"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
            <span>{t('admin.products.detail.deleteAction')}</span>
          </Button>
          <Button asChild data-testid="product-detail-edit">
            <Link to={editPath}>
              <Pencil className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
              <span>{t('admin.products.detail.editAction')}</span>
            </Link>
          </Button>
        </div>
      </header>

      {/* Two-column layout: gallery on inline-start, metadata on inline-end */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-start">
        <ProductImageGallery images={product.images} productName={product.name} />

        <aside
          className="border-border bg-card shadow-admin-card flex flex-col gap-4 rounded-lg border p-6 lg:sticky lg:top-24"
          data-testid="product-detail-metadata"
        >
          {/* Status badge */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="sr-only">{t('admin.products.detail.statusLabel')}</span>
            <Badge variant={statusVariant} data-testid="product-detail-status-badge">
              {t(statusKey)}
            </Badge>
            <Badge variant={availabilityVariant}>{t(availabilityKey)}</Badge>
            {salePricing.isOnSale ? (
              <>
                <Badge variant="warning" data-testid="product-detail-sale-badge">
                  {t('admin.products.badge.onSale')}
                </Badge>
                {salePricing.discountPercent !== null ? (
                  <Badge variant="default" data-testid="product-detail-discount-badge">
                    {t('admin.products.badge.discountPercent', {
                      percent: salePricing.discountPercent,
                    })}
                  </Badge>
                ) : null}
              </>
            ) : null}
          </div>

          {/* F-028: Publish / Unpublish button + helper. Sits between
              status badge and product name. Disable-state priority:
              !storePublished wins over inventory === 0. */}
          <div>
            {product.published ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onUnpublishClick}
                data-testid="product-detail-unpublish"
                className="text-muted-foreground hover:bg-admin-surface-muted hover:text-foreground"
              >
                <EyeOff className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                <span>{t('admin.products.publish.buttons.unpublish')}</span>
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="default"
                  onClick={onPublishClick}
                  disabled={!storePublished || product.inventory <= 0 || !hasCategory}
                  data-testid="product-detail-publish"
                >
                  <Globe className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                  <span>{t('admin.products.publish.buttons.publish')}</span>
                </Button>
                {!storePublished ? (
                  <p className="text-muted-foreground mt-1.5 text-start text-xs">
                    {t('admin.products.publish.disabled.storeNotPublished')}{' '}
                    <Link
                      to="/dashboard"
                      data-testid="product-detail-publish-store-cta"
                      className="text-zaatar hover:text-zaatar-deep font-medium underline"
                    >
                      {t('admin.products.publish.disabled.storeNotPublishedCta')}
                    </Link>
                  </p>
                ) : product.inventory === 0 ? (
                  <p className="text-muted-foreground mt-1.5 text-start text-xs">
                    {t('admin.products.publish.disabled.insufficientInventory')}{' '}
                    <Link
                      to={`/products/${product.id}/edit`}
                      data-testid="product-detail-publish-inventory-cta"
                      className="text-zaatar hover:text-zaatar-deep font-medium underline"
                    >
                      {t('admin.products.publish.disabled.insufficientInventoryCta')}
                    </Link>
                  </p>
                ) : !hasCategory ? (
                  <p className="text-muted-foreground mt-1.5 text-start text-xs">
                    {t('admin.products.publish.disabled.categoryRequired')}{' '}
                    <Link
                      to={`/products/${product.id}/edit`}
                      data-testid="product-detail-publish-category-cta"
                      className="text-zaatar hover:text-zaatar-deep font-medium underline"
                    >
                      {t('admin.products.publish.disabled.categoryRequiredCta')}
                    </Link>
                  </p>
                ) : null}
              </>
            )}
          </div>

          {/* Product name */}
          <h1
            className="font-display text-foreground text-start text-[28px] font-semibold leading-tight"
            data-testid="product-detail-name"
          >
            {product.name}
          </h1>

          {/* Price */}
          <div>
            <span className="sr-only">{t('admin.products.detail.priceLabel')}</span>
            <p
              className="font-display text-saffron text-start text-[32px] font-semibold leading-none"
              data-testid="product-detail-price"
            >
              {formattedPrice}
            </p>
            {salePricing.isOnSale ? (
              <bdi
                className="text-muted-foreground mt-2 block text-start text-sm line-through"
                data-testid="product-detail-regular-price"
              >
                {formattedRegularPrice}
              </bdi>
            ) : null}
          </div>

          {/* Inventory */}
          <p
            className={cn('text-start text-[15px]', inventoryClass)}
            data-testid="product-detail-inventory"
          >
            {inventoryText}
          </p>

          <div className="flex flex-col gap-2">
            <p className="text-muted-foreground text-start text-[13px] font-medium">
              {t('admin.products.detail.categoryLabel')}
            </p>
            <Badge
              variant="neutral"
              className="w-fit max-w-full truncate"
              data-testid="product-detail-category"
            >
              {hasCategory ? product.category.name : t('admin.products.detail.categoryMissing')}
            </Badge>
          </div>

          {product.description ? (
            <>
              <div className="bg-border h-px w-full" aria-hidden="true" />
              <div className="flex flex-col gap-2">
                <p className="text-muted-foreground text-start text-[13px] font-medium">
                  {t('admin.products.detail.descriptionLabel')}
                </p>
                <p
                  className="text-foreground whitespace-pre-wrap text-start text-base leading-relaxed"
                  data-testid="product-detail-description"
                >
                  {product.description}
                </p>
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
